import { ArrowRight, BookOpen, Eye, Compass, PenTool, CheckCircle, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { Badge } from '../ui/badge';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { PURCHASE_URL, PRODUCT_IMAGES, MODULES } from '../../data/mock';
import { trackPurchaseClick } from '../../utils/pixel';

const handlePurchase = () => {
  trackPurchaseClick();
  window.open(PURCHASE_URL, '_blank', 'noopener,noreferrer');
};

const moduleIcons = [<Eye size={20} />, <PenTool size={20} />, <Compass size={20} />, <BookOpen size={20} />];
const moduleColors = ['#EC4899', '#DB2777', '#BE185D', '#9D174D'];

export const ProductSection = () => {
  const [ref1, vis1] = useScrollReveal();
  const [ref2, vis2] = useScrollReveal();
  const [ref3, vis3] = useScrollReveal();
  const [ref4, vis4] = useScrollReveal();

  return (
    <>
      {/* Product Introduction */}
      <section id="manual" className="py-20 md:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #FDF2F8 0%, #FCE7F3 30%, #FBCFE8 60%, #FCE7F3 100%)' }}>
        <div className="absolute top-0 left-0 w-[400px] h-[400px] rounded-full opacity-12" style={{ background: 'radial-gradient(circle, #F9A8D4 0%, transparent 70%)' }} />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* 3D Product Image - clean */}
            <div ref={ref1} className={`reveal-left ${vis1 ? 'visible' : ''} flex justify-center order-2 lg:order-1`}>
              <div className="perspective-container">
                <div className="product-3d relative">
                  <img src={PRODUCT_IMAGES.main} alt="Manual Cejas Radiantes" className="w-full max-w-md rounded-2xl" style={{ objectFit: 'cover' }} />
                </div>
              </div>
            </div>

            {/* Text */}
            <div ref={ref2} className={`reveal-right ${vis2 ? 'visible' : ''} order-1 lg:order-2`}>
              <Badge className="mb-6 font-body text-xs rounded-full px-4 py-1.5 border-0" style={{ backgroundColor: 'rgba(252,231,243,0.9)', color: '#9D174D' }}>El Manual</Badge>
              <div className="space-y-6 font-body text-lg leading-relaxed" style={{ color: '#3D2E50' }}>
                <p>
                  El Sistema de Diseño con Certeza no es teoría aislada.
                  Está organizado paso a paso dentro del <span className="font-bold" style={{ color: '#9D174D' }}>Manual Cejas Radiantes.</span>
                </p>
                <p>Un manual digital diseñado para que puedas entender el diseño desde la base, no desde fragmentos sueltos.</p>
                <p className="font-semibold" style={{ color: '#831843' }}>Dentro encuentras la estructura completa del proceso:</p>
                <div className="space-y-3">
                  {[
                    'Cómo analizar el rostro antes de trazar.',
                    'Cómo construir la forma con proporción.',
                    'Cómo ajustar el diseño según cada caso.',
                    'Cómo tomar decisiones con fundamento, no por intuición.'
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 glass-strong rounded-xl px-4 py-3 hover-glow">
                      <CheckCircle size={18} className="mt-0.5 flex-shrink-0" style={{ color: '#DB2777' }} />
                      <span className="text-base">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="glass-pink rounded-2xl p-6 mt-4">
                  <p className="font-display text-xl italic" style={{ color: '#9D174D' }}>No es una colección de tips. Es un mapa.</p>
                </div>
                <p className="text-base">
                  Un recurso que puedes consultar mientras practicas, para confirmar cada paso y avanzar con mayor claridad en cada diseño.
                </p>
                <p className="font-semibold" style={{ color: '#831843' }}>
                  Porque cuando tienes una base estructurada frente a ti,
                  la práctica deja de ser ensayo y error.
                  Se convierte en construcción consciente.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visualization / Imagina */}
      <section className="py-20 md:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, rgba(252,231,243,0.7) 0%, rgba(251,207,232,0.5) 50%, rgba(253,242,248,0.8) 100%)' }}>
        <div className="absolute top-1/3 right-0 w-[350px] h-[350px] rounded-full opacity-12" style={{ background: 'radial-gradient(circle, #F472B6 0%, transparent 70%)' }} />

        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div ref={ref3} className={`reveal ${vis3 ? 'visible' : ''}`}>
            <div className="text-center space-y-8">
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full mx-auto" style={{ backgroundColor: 'rgba(252,231,243,0.9)', border: '1px solid rgba(249,168,212,0.3)' }}>
                <Sparkles size={14} style={{ color: '#DB2777' }} />
                <span className="font-body text-xs font-semibold uppercase tracking-wider" style={{ color: '#9D174D' }}>Visualiza tu transformación</span>
              </div>

              <h3 className="font-display text-3xl sm:text-4xl font-bold gradient-text">Imagina...</h3>

              <div className="grid sm:grid-cols-2 gap-5 text-left">
                {[
                  'Empezar un diseño y saber exactamente por dónde comenzar.',
                  'Analizar el rostro sin sentir que estás improvisando.',
                  'Trazar con intención. Ajustar con criterio.',
                  'Sin esa presión silenciosa de estar adivinando.'
                ].map((item, i) => (
                  <div key={i} className="glass-strong rounded-2xl p-5 hover-lift">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: 'rgba(252,231,243,0.9)', color: '#DB2777' }}>
                        <Sparkles size={14} />
                      </div>
                      <p className="font-body text-base leading-relaxed" style={{ color: '#3D2E50' }}>{item}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="font-body text-lg" style={{ color: '#3D2E50' }}>
                Imagina terminar el diseño y sentir tranquilidad.
                No porque "salió bien por suerte".
                Sino porque entendiste cada paso que diste.
              </p>

              <div className="relative py-6">
                <div className="inline-block glass-pink rounded-3xl px-10 py-8 shimmer-border">
                  <p className="font-display text-2xl italic" style={{ color: '#831843' }}>
                    Tu clienta se mira al espejo.<br />
                    Sonríe.<br />
                    Y tú también.
                  </p>
                </div>
              </div>

              <p className="font-body text-lg" style={{ color: '#3D2E50' }}>
                Porque esta vez no estás cruzando los dedos para que vuelva.
                <span className="font-bold" style={{ color: '#BE185D' }}> Sabes que volverá.</span>
                No por casualidad. Sino porque trabajaste con fundamento.
              </p>

              <p className="font-display text-xl font-bold pt-2" style={{ color: '#9D174D' }}>
                Esa es la diferencia entre practicar...<br />
                y diseñar con certeza profesional.
              </p>

              <div className="pt-6">
                <Button onClick={handlePurchase} size="lg" className="cta-pulse rounded-full text-white font-body font-bold text-base px-10 py-6 h-auto" style={{ backgroundColor: '#DB2777' }}>
                  Acceder al Manual Ahora
                  <ArrowRight size={18} className="ml-1" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section id="contenido" className="py-20 md:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #FDF2F8 0%, #FCE7F3 30%, #FBCFE8 60%, #FCE7F3 100%)' }}>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #F9A8D4 0%, transparent 70%)' }} />

        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div ref={ref4} className={`reveal ${vis4 ? 'visible' : ''}`}>
            <div className="text-center mb-14">
              <Badge className="mb-4 font-body text-xs rounded-full px-4 py-1.5 border-0" style={{ backgroundColor: 'rgba(252,231,243,0.9)', color: '#9D174D' }}>Contenido Completo</Badge>
              <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#1F1235' }}>
                ¿Qué incluye el Manual de Cejas Radiantes?
              </h2>
              <p className="font-body text-lg max-w-2xl mx-auto" style={{ color: '#4A3B5C' }}>
                El manual está organizado para que comprendas el diseño desde la base técnica hasta la ejecución profesional. No son temas aislados. Es una estructura progresiva.
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {MODULES.map((mod, idx) => (
                <AccordionItem key={idx} value={`module-${idx}`} className="glass-strong rounded-2xl px-6 border-0 hover-glow overflow-hidden" style={{ borderBottom: 'none' }}>
                  <AccordionTrigger className="hover:no-underline py-6">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'rgba(252,231,243,0.9)', color: moduleColors[idx] }}>
                        {moduleIcons[idx]}
                      </div>
                      <div className="text-left">
                        <p className="font-body text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: moduleColors[idx] }}>Módulo {mod.number}</p>
                        <p className="font-display text-lg font-bold" style={{ color: '#1F1235' }}>{mod.title}</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <p className="font-body text-sm italic mb-5 pl-16" style={{ color: '#9D174D' }}>{mod.subtitle}</p>
                    <div className="space-y-3 pl-16">
                      {mod.items.map((item, iIdx) => (
                        <div key={iIdx} className="glass-pink rounded-xl p-4 hover-glow">
                          <p className="font-body text-sm font-semibold mb-1" style={{ color: '#1F1235' }}>{item.title}</p>
                          <p className="font-body text-sm leading-relaxed" style={{ color: '#4A3B5C' }}>{item.description}</p>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </>
  );
};
